import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookMarked, Loader2, Trash2, ImageOff, Moon } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  story_everyone: "Everyone",
  story_kids: "Kids",
  story_adults: "Adults",
  story_comic: "Comic",
  story_descriptive: "Descriptive",
  story_location: "Location",
};

export default function Stories() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/stories");
      setStories(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!stories.some((s) => s.cover_status === "pending")) return;
    let polls = 0;
    const id = setInterval(() => {
      polls += 1;
      if (polls > 30) return clearInterval(id);
      load();
    }, 6000);
    return () => clearInterval(id);
  }, [stories]);

  const remove = async (story) => {
    setStories((list) => list.filter((s) => s.story_id !== story.story_id));
    try {
      await api.delete(`/stories/${story.story_id}`);
      toast.success("Story removed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      load();
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8" data-testid="stories-page">
      <Link to="/chat?persona=storyteller" data-testid="stories-back" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand">
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> Back to Storytelling
      </Link>
      <div className="mt-6 flex items-start gap-4">
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/15 text-brand">
          <BookMarked className="h-6 w-6" />
        </span>
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">Story Library</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-soft md:text-base">
            Every story you finished, with its illustrated cover. Reopen it, keep writing, or share the link.
          </p>
          <Link
            to="/bedtime"
            data-testid="bedtime-link"
            className="mt-4 inline-flex min-h-[40px] items-center gap-2 rounded-full bg-brand/15 px-5 text-sm text-brand hover:bg-brand/25"
          >
            <Moon className="h-4 w-4" /> Bedtime playlist
          </Link>
        </div>
      </div>

      {loading && <Loader2 className="mt-10 h-6 w-6 animate-spin text-brand" data-testid="stories-loading" />}

      {!loading && stories.length === 0 && (
        <div data-testid="stories-empty" className="mt-10 rounded-3xl border border-white/10 bg-panel p-10 text-center">
          <p className="text-sm text-ink-soft">
            No stories yet. Finish one in Storytelling and it lands here with a cover illustration.
          </p>
          <Button asChild className="mt-6 min-h-[44px] rounded-full bg-brand px-6 hover:bg-brand-hover">
            <Link to="/chat?persona=storyteller" data-testid="stories-go-storyteller">Start a story</Link>
          </Button>
        </div>
      )}

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="stories-grid">
        {stories.map((s) => (
          <div
            key={s.story_id}
            data-testid={`story-card-${s.story_id}`}
            className="group overflow-hidden rounded-3xl border border-white/10 bg-panel transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40"
          >
            <Link to={`/stories/${s.story_id}`} className="block">
              <div className="flex aspect-[4/3] items-center justify-center bg-page">
                {s.cover_creation_id ? (
                  <AuthImage
                    creationId={s.cover_creation_id}
                    alt={s.title}
                    className="h-full w-full object-cover"
                    testid={`story-cover-${s.story_id}`}
                  />
                ) : s.cover_status === "pending" ? (
                  <span className="flex flex-col items-center gap-2 text-xs text-ink-soft">
                    <Loader2 className="h-5 w-5 animate-spin text-brand" /> Illustrating cover…
                  </span>
                ) : (
                  <ImageOff className="h-6 w-6 text-ink-soft" />
                )}
              </div>
            </Link>
            <div className="space-y-2 p-4">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-brand/15 px-2.5 py-1 text-xs font-semibold text-brand">
                  {KIND_LABEL[s.persona] || "Story"}
                </span>
                {s.series_index > 1 && (
                  <span
                    data-testid={`story-part-${s.story_id}`}
                    className="rounded-full bg-forest/20 px-2.5 py-1 text-xs font-semibold text-forest"
                  >
                    Part {s.series_index}
                  </span>
                )}
                <span className="text-xs text-ink-soft">{(s.created_at || "").slice(0, 10)}</span>
                <button
                  data-testid={`story-delete-${s.story_id}`}
                  onClick={() => remove(s)}
                  className="ms-auto text-ink-soft transition-colors hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <Link
                to={`/stories/${s.story_id}`}
                dir="auto"
                className="block font-display text-base font-bold leading-snug tracking-tight hover:text-brand"
              >
                {s.title}
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
